import { useState } from 'react';
import { useWidth } from './useWidth';
import { Badge } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';
import { statusLabel } from '@/data/studio-layouts';
import type { PublishState, ReviewStatus } from '@/types/social';
import type { LibraryBlogItem, LibraryItem, LibrarySocialItem } from '@/data/sources-library';
import { blogStatusLabel, channelName, channelShort, formatDay, itemKey, sourceLabel, targetLabel, wpLabel } from './libraryModel';

/* Table (desktop) and card (<= 760px) renderers for Library rows. Every row shows
 * the decision data: target page, keyword, site, status, WordPress state, date. */

function socialStatus(i: LibrarySocialItem) {
  return statusLabel(i.status as ReviewStatus, i.publish_state as PublishState, i.scheduled_at, i.provider_ref_present ? 'ref' : null);
}

export function ItemStatus({ item }: { item: LibraryItem }) {
  const s = item.kind === 'blog' ? blogStatusLabel(item.status) : socialStatus(item);
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function WpBadge({ item }: { item: LibraryBlogItem }) {
  const w = wpLabel(item.wp.state);
  return <span title={item.wp.state === 'failed' && item.wp.error ? item.wp.error : undefined}><Badge tone={w.tone}>{w.label}</Badge></span>;
}

function ChannelChips({ variants }: { variants: LibrarySocialItem[] }) {
  if (!variants.length) return null;
  return (
    <span className="inline-flex flex-wrap gap-0.5">
      {variants.map((c) => {
        const published = c.publish_state === 'published' && c.provider_ref_present;
        return (
          <span key={c.id} title={`${channelName(c.channel)}: ${socialStatus(c).label}`}
            className={cn('rounded px-1 font-mono text-[10px] font-semibold', published ? 'bg-success/10 text-success' : 'bg-inset text-text-secondary')}>
            {channelShort(c.channel)}
          </span>
        );
      })}
    </span>
  );
}

function Title({ item, onPreview, tall }: { item: LibraryItem; onPreview: () => void; tall?: boolean }) {
  const title = item.title || (item.kind === 'blog' ? 'Untitled draft' : 'Untitled post');
  return (
    <button onClick={onPreview} className={cn('block max-w-full truncate text-left text-sm font-medium text-text-primary hover:text-ai', tall && 'min-h-[44px]')} title={title}>
      {title}
    </button>
  );
}

function SubLine({ item }: { item: LibraryItem }) {
  return (
    <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5 text-[11px] text-text-secondary">
      {item.domain && <Badge tone={item.domain.unassigned ? 'neutral' : 'ai'}>{item.domain.label}</Badge>}
      {item.kind === 'blog' ? <span className="truncate">/{item.slug || 'no-slug'}</span>
        : <span>{channelName(item.channel)} · {item.format === 'carousel' ? `Carousel · ${item.pages}p` : 'Single'}</span>}
      <span>· {sourceLabel(item.source)}</span>
      {item.kind === 'social' && item.parent && <span className="truncate">· from blog “{item.parent.title}”</span>}
    </div>
  );
}

function Target({ item }: { item: LibraryItem }) {
  if (item.target_page) return <span className="block truncate" title={item.target_page}>{targetLabel(item.target_page)}</span>;
  if (item.kind === 'blog') return <span className="text-text-secondary">Best-matched page (not recorded)</span>;
  return <span className="text-text-secondary">—</span>;
}

export interface RowHandlers {
  selected: Set<string>;
  toggle: (item: LibraryItem) => void;
  preview: (item: LibraryItem) => void;
  open: (item: LibraryItem) => void;
}

export function LibraryTable({ items, h, allSelected, toggleAll }: { items: LibraryItem[]; h: RowHandlers; allSelected: boolean; toggleAll: () => void }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  // Below ~1000px of available width the target page + keyword move under the
  // title (same data, fewer columns) so nothing is clipped or scrolled sideways.
  const full = width === 0 || width >= 1000;
  const flip = (id: number) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <div ref={wrapRef} className="mt-3 overflow-x-auto rounded-md border border-border-subtle bg-surface">
      <table className={cn('w-full table-fixed border-collapse text-sm', full ? 'min-w-[1000px]' : 'min-w-[600px]')}>
        <caption className="sr-only">Content library</caption>
        <colgroup>
          <col className="w-9" /><col /><col className="w-[84px]" />
          {full && <><col className="w-[170px]" /><col className="w-[140px]" /></>}
          <col className="w-[124px]" /><col className="w-[104px]" /><col className="w-[92px]" /><col className="w-[64px]" />
        </colgroup>
        <thead>
          <tr className="border-b border-border-subtle text-left text-[11px] uppercase tracking-wide text-text-secondary">
            <th className="px-2 py-2"><input type="checkbox" aria-label="Select all on this page" checked={allSelected} onChange={toggleAll} /></th>
            <th className="px-2 py-2 font-semibold">Title</th>
            <th className="px-2 py-2 font-semibold">Type</th>
            {full && <th className="px-2 py-2 font-semibold">Target page / listing</th>}
            {full && <th className="px-2 py-2 font-semibold">Keyword</th>}
            <th className="px-2 py-2 font-semibold">Status</th>
            <th className="px-2 py-2 font-semibold">WordPress</th>
            <th className="px-2 py-2 font-semibold">Updated</th>
            <th className="px-2 py-2"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const kids = item.kind === 'blog' ? item.children : [];
            const isOpen = item.kind === 'blog' && expanded.has(item.id);
            return [
              <tr key={itemKey(item)} className="border-b border-border-subtle align-top hover:bg-hover/40">
                <td className="px-2 py-2.5"><input type="checkbox" aria-label={`Select ${item.title || 'item'}`} checked={h.selected.has(itemKey(item))} onChange={() => h.toggle(item)} /></td>
                <td className="min-w-0 px-2 py-2.5">
                  <div className="flex min-w-0 items-start gap-1">
                    {kids.length > 0 ? (
                      <button onClick={() => flip(item.id)} aria-expanded={isOpen} aria-label={`${isOpen ? 'Hide' : 'Show'} ${kids.length} social posts`}
                        className="mt-0.5 w-4 shrink-0 text-xs text-text-secondary hover:text-text-primary">{isOpen ? '▾' : '▸'}</button>
                    ) : <span className="w-4 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <Title item={item} onPreview={() => h.preview(item)} /><SubLine item={item} />
                      {!full && (
                        <div className="mt-0.5 flex min-w-0 gap-2 text-[11px] text-text-secondary">
                          <span className="min-w-0 truncate">→ <Target item={item} /></span>
                          {item.keyword && <span className="min-w-0 truncate">kw: {item.keyword}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2.5 text-xs text-text-secondary">
                  <div>{item.kind === 'blog' ? 'Blog' : 'Social'}</div>
                  {item.kind === 'blog' ? <ChannelChips variants={item.children} /> : <span className="rounded bg-inset px-1 font-mono text-[10px] font-semibold">{channelShort(item.channel)}</span>}
                </td>
                {full && <td className="truncate px-2 py-2.5 text-xs text-text-secondary"><Target item={item} /></td>}
                {full && <td className="truncate px-2 py-2.5 text-xs text-text-secondary" title={item.keyword ?? undefined}>{item.keyword ?? <span className="text-text-secondary">—</span>}</td>}
                <td className="px-2 py-2.5"><ItemStatus item={item} /></td>
                <td className="px-2 py-2.5">{item.kind === 'blog' ? <WpBadge item={item} /> : <span className="text-text-secondary">—</span>}</td>
                <td className="px-2 py-2.5 font-mono text-xs tabular-nums text-text-secondary">{formatDay(item.updated_at)}</td>
                <td className="px-2 py-2.5 text-right">
                  <button onClick={() => h.open(item)} className="rounded-md border border-border-default px-2 py-1 text-xs font-medium text-text-primary hover:bg-hover">Open</button>
                </td>
              </tr>,
              ...(isOpen ? kids.map((c) => (
                <tr key={'c' + itemKey(c)} className={cn('border-b border-border-subtle bg-inset/40 align-top text-xs', c.matches === false && 'opacity-70')}>
                  <td className="px-2 py-2"><input type="checkbox" aria-label={`Select ${channelName(c.channel)} post`} checked={h.selected.has(itemKey(c))} onChange={() => h.toggle(c)} /></td>
                  <td className="px-2 py-2 pl-8">
                    <button onClick={() => h.preview(c)} className="text-left font-medium text-text-primary hover:text-ai">{channelName(c.channel)} · {c.format === 'carousel' ? 'Carousel' : 'Single'}</button>
                    <div className="truncate text-[11px] text-text-secondary" title={c.excerpt}>{c.excerpt || 'No copy yet'}</div>
                  </td>
                  <td className="px-2 py-2 text-text-secondary">Social</td>
                  {full && <td className="px-2 py-2 text-text-secondary" colSpan={2}>Same target as the blog</td>}
                  <td className="px-2 py-2"><ItemStatus item={c} /></td>
                  <td className="px-2 py-2" />
                  <td className="px-2 py-2 font-mono tabular-nums text-text-secondary">{formatDay(c.updated_at)}</td>
                  <td className="px-2 py-2 text-right"><button onClick={() => h.open(c)} className="rounded-md border border-border-default px-2 py-1 text-xs font-medium hover:bg-hover">Open</button></td>
                </tr>
              )) : []),
            ];
          })}
        </tbody>
      </table>
    </div>
  );
}

export function LibraryCards({ items, h }: { items: LibraryItem[]; h: RowHandlers }) {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const flip = (id: number) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <ul className="mt-3 flex flex-col gap-2">
      {items.map((item) => {
        const kids = item.kind === 'blog' ? item.children : [];
        const isOpen = item.kind === 'blog' && expanded.has(item.id);
        return (
          <li key={itemKey(item)} className="min-w-0 rounded-md border border-border-subtle bg-surface p-3">
            <div className="flex min-w-0 items-start gap-2">
              <label className="-m-2 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center">
                <input type="checkbox" className="h-5 w-5" aria-label={`Select ${item.title || 'item'}`} checked={h.selected.has(itemKey(item))} onChange={() => h.toggle(item)} />
              </label>
              <div className="min-w-0 flex-1">
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <div className="min-w-0 flex-1"><Title item={item} onPreview={() => h.preview(item)} tall /></div>
                  <ItemStatus item={item} />
                </div>
                <SubLine item={item} />
                <div className="mt-1.5 flex min-w-0 flex-wrap gap-x-3 gap-y-1 text-[11px] text-text-secondary">
                  <span>{item.kind === 'blog' ? 'Blog' : channelName(item.channel)}</span>
                  {item.kind === 'blog' && <ChannelChips variants={item.children} />}
                  <span className="min-w-0 max-w-full truncate">→ <Target item={item} /></span>
                  {item.keyword && <span className="min-w-0 max-w-full truncate">kw: {item.keyword}</span>}
                  {item.kind === 'blog' && <WpBadge item={item} />}
                  <span className="font-mono tabular-nums">{formatDay(item.updated_at)}</span>
                </div>
                {item.kind === 'social' && item.excerpt && <p className="mt-1 line-clamp-2 text-xs text-text-secondary">{item.excerpt}</p>}
                <div className="mt-2 flex flex-wrap gap-2">
                  <button onClick={() => h.preview(item)} className="min-h-[44px] rounded-md border border-border-default px-3 text-xs font-medium">Preview</button>
                  <button onClick={() => h.open(item)} className="min-h-[44px] rounded-md border border-border-default px-3 text-xs font-medium">Open</button>
                  {kids.length > 0 && (
                    <button onClick={() => flip(item.id)} aria-expanded={isOpen} className="min-h-[44px] rounded-md px-3 text-xs font-medium text-ai">
                      {kids.length} social {kids.length === 1 ? 'post' : 'posts'} {isOpen ? '▴' : '▾'}
                    </button>
                  )}
                </div>
                {isOpen && (
                  <ul className="mt-2 flex flex-col gap-1.5 border-l border-border-subtle pl-3">
                    {kids.map((c) => (
                      <li key={c.id} className="flex min-w-0 items-center justify-between gap-2 text-xs">
                        <button onClick={() => h.preview(c)} className="min-h-[44px] min-w-0 flex-1 truncate text-left text-text-primary">{channelName(c.channel)} · {c.excerpt || 'No copy yet'}</button>
                        <ItemStatus item={c} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
